// app/helpers/interactions/handlersMouseDown.js
import {
  calculateAngle,
  calculatePolygonArea,
  calculatePolygonPerimeter,
  findClosestEdge,
  reopenPolygonAt,
  reorderPolygonPoints,
} from "../geometry";
import {
  // isPointNearPoint,
  isPointOnLine,
  isPointInPolygon,
  isPointOnPolygonEdge,
  getTextBounds,
  getTextResizeHandle,
  isPointNearGuideline,
  isPointNearPoint,
} from "../hitTest";
import { distUnits } from "./context";

// --- unit helpers (px ↔ mm) ---
const isUnitMode = (ctx) =>
  Number(ctx.scale) > 0 && Number(ctx.scaleLength) > 0;

export const computeDistance = (start, end, ctx) => {
  // (measureLineDistance / referenceLineDistance) * unit   OR   px
  const dx = (end?.x ?? 0) - (start?.x ?? 0);
  const dy = (end?.y ?? 0) - (start?.y ?? 0);
  const dpx = Math.hypot(dx, dy);
  if (!isUnitMode(ctx)) return dpx; // px
  return (dpx / Number(ctx.scaleLength)) * Number(ctx.scale); // units (e.g., mm)
};

export const computeArea = (points, ctx) => {
  // px²  OR  units² using (units/px)^2
  const scale = isUnitMode(ctx) ? Number(ctx.scale) : 0;
  const scaleLength = isUnitMode(ctx) ? Number(ctx.scaleLength) : 0;
  return calculatePolygonArea(points, scale, scaleLength, ctx.fabricCanvasRef);
};

export const computePerimeter = (points, ctx) => {
  // perimeter uses the same distance logic (mm if unit mode, else px)
  return calculatePolygonPerimeter(points, (a, b) =>
    computeDistance(a, b, ctx)
  );
};

export function makeHandleMouseDown(ctx) {
  return function handleMouseDown(event, fabricPoint) {
    // ---------- RESIZE via blue floating handle (with object remap) ----------
    // --- CSS-ONLY RESIZE via blue handle (no object/bg remap) ---------------
    const role =
      event?.target?.dataset?.role ||
      event?.currentTarget?.dataset?.role ||
      (event?.target?.closest?.("[data-role='canvas-resizer']") &&
        "canvas-resizer");

    if (role === "canvas-resizer") {
      event.preventDefault?.();
      event.stopPropagation?.();

      const startX = event.clientX;
      const startY = event.clientY;

      // visual size we will change
      const startW = Number(ctx.displaySize?.width) || 1;
      const startH = Number(ctx.displaySize?.height) || 1;

      // optional: keep aspect ratio of backstore
      const ar =
        (Number(ctx.canvasSize?.height) || startH) /
          (Number(ctx.canvasSize?.width) || startW) || 1;

      const applyCssOnly = (w, h, phase = "move") => {
        // 1) update visual state ONLY
        ctx.onDisplaySizeChange?.({ width: w, height: h });
        ctx.displaySize = { width: w, height: h };

        // 2) resize Fabric canvases via CSS only
        const fc = ctx.fabricCanvasRef?.current;
        if (fc) {
          try {
            fc.setDimensions({ width: w, height: h }, { cssOnly: true });
          } catch (err) {
            console.warn(
              "[resize] cssOnly setDimensions failed, fallback",
              err
            );
            if (fc.lowerCanvasEl) {
              fc.lowerCanvasEl.style.width = `${w}px`;
              fc.lowerCanvasEl.style.height = `${h}px`;
            }
            if (fc.upperCanvasEl) {
              fc.upperCanvasEl.style.width = `${w}px`;
              fc.upperCanvasEl.style.height = `${h}px`;
            }
          }
          fc.calcOffset && fc.calcOffset();
          fc.requestRenderAll && fc.requestRenderAll();
        } else {
          console.warn("[resize] no fabric canvas");
        }
      };

      const onMove = (e) => {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        // corner drag w/ AR (switch to freeform if you prefer)
        let newW = Math.max(100, startW + dx);
        let newH = Math.round(newW * ar);
        applyCssOnly(newW, newH, "move");
      };

      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        document.body.style.userSelect = "";
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      document.body.style.userSelect = "none";
      return; // stop other tools during resize
    }
    // ------------------------------------------------------------------------

    // ---------- /RESIZE ----------------------------------------------------------

    const point = fabricPoint || ctx.getCanvasPoint(event);

    const canvas = ctx.canvasRef.current;
    if (!canvas) return;

    // Pan
    if (
      ctx.isPanMode ||
      ctx.activeTool === "pan" ||
      ("button" in event &&
        (event.button === 1 || (event.button === 0 && event.ctrlKey)))
    ) {
      return;
      ctx.setIsPanning(true);
      ctx.setLastPanPoint(point);
      if (ctx.canvasRef.current) ctx.canvasRef.current.selection = false;

      return;
    }

    // DOT (single click to add a circle)
    if (ctx.activeTool === "dot") {
      const point = fabricPoint || ctx.getCanvasPoint(event);
      const dot = {
        id: Date.now().toString(),
        position: { x: point.x, y: point.y },
        // radius: 5,
        // color: "#00E5FF",
      };
      ctx.setDots((prev) => [...prev, dot]);
      ctx.addToHistory?.();
      ctx.setActiveTool("select");
      return;
    }

    // CREATE on click (like dot)
    if (ctx.activeTool === "arrow") {
      const p = fabricPoint || ctx.getCanvasPoint(event);
      const id = String(Date.now());
      const arrow = {
        id,
        start: { x: p.x, y: p.y },
        end: { x: p.x + 100, y: p.y }, // small default so it's visible
        // color: "#ff0008ff",
      };
      ctx.setArrows?.((prev) => [...(prev || []), arrow]);
      // ctx.setSelectedArrowId?.(id);
      ctx.addToHistory?.();
      ctx.setActiveTool("select");
      return;
    }

    // Scale tool hits
    // if (ctx.activeTool === "scale") {
    const end = {
      x: ctx.scalePosition.x + ctx.scaleLength * Math.cos(ctx.scaleAngle),
      y: ctx.scalePosition.y + ctx.scaleLength * Math.sin(ctx.scaleAngle),
    };

    if (isPointNearPoint(point, ctx.scalePosition, 15)) {
      ctx.setIsDraggingScale(true);
      ctx.setSelectedMeasurement("scale");
      ctx.setDragOffset({
        x: point.x - ctx.scalePosition.x,
        y: point.y - ctx.scalePosition.y,
      });
      return;
    } else if (isPointNearPoint(point, end, 15)) {
      ctx.setIsDraggingScaleEnd(true);
      ctx.setSelectedMeasurement("scale");
      ctx.setDragOffset({ x: point.x - end.x, y: point.y - end.y });
      return;
    } else if (isPointOnLine(point, ctx.scalePosition, end, 10)) {
      ctx.setIsDraggingScale(true);
      ctx.setSelectedMeasurement("scale");
      ctx.setDragOffset({
        x: point.x - ctx.scalePosition.x,
        y: point.y - ctx.scalePosition.y,
      });
      return;
    }
    // }

    // Selection
    if (ctx.activeTool === "select") {
      // Dots
      // if (Array.isArray(ctx.dots) && ctx.dots.length) {
      //   for (const d of ctx.dots) {
      //     if (isPointNearPoint(point, d.position, 10)) {
      //       ctx.setSelectedDot?.(d.id);
      //       ctx.setSelectedMeasurement?.(null);
      //       ctx.setSelectedText?.(null);
      //       ctx.setSelectedPolygon?.(null);
      //       ctx.setSelectedGuideline?.(null);
      //       ctx.setIsDraggingDot?.(true);
      //       ctx.setDraggedDotId?.(d.id);
      //       ctx.setDotDragOffset?.({
      //         x: point.x - d.position.x,
      //         y: point.y - d.position.y,
      //       });
      //       // prevent Fabric marquee
      //       if (ctx.canvasRef.current) ctx.canvasRef.current.selection = false;
      //       return;
      //     }
      //   }
      // }
      // Dots (LOWEST priority vs lines/angles)
      if (Array.isArray(ctx.dots) && ctx.dots.length) {
        // If click is near a line/angle, let that win later
        const TOUCH_EPS = 8;

        const nearLinear = (ctx.measurements || []).some(
          (m) =>
            "start" in m &&
            "end" in m &&
            (isPointNearPoint(point, m.start, 10) ||
              isPointNearPoint(point, m.end, 10) ||
              isPointOnLine(point, m.start, m.end, TOUCH_EPS))
        );

        const nearAngle = (ctx.measurements || []).some(
          (m) =>
            !("start" in m) &&
            "center" in m &&
            "point1" in m &&
            "point2" in m &&
            (isPointNearPoint(point, m.center, 10) ||
              isPointNearPoint(point, m.point1, 10) ||
              isPointNearPoint(point, m.point2, 10) ||
              isPointOnLine(point, m.center, m.point1, TOUCH_EPS) ||
              isPointOnLine(point, m.center, m.point2, TOUCH_EPS))
        );

        // Skip dot selection if also over a line/angle; let later blocks handle it
        if (!nearLinear && !nearAngle) {
          for (const d of ctx.dots) {
            if (isPointNearPoint(point, d.position, 10)) {
              ctx.setSelectedDot?.(d.id);
              ctx.setSelectedMeasurement?.(null);
              ctx.setSelectedText?.(null);
              ctx.setSelectedPolygon?.(null);
              ctx.setSelectedGuideline?.(null);
              ctx.setIsDraggingDot?.(true);
              ctx.setDraggedDotId?.(d.id);
              ctx.setDotDragOffset?.({
                x: point.x - d.position.x,
                y: point.y - d.position.y,
              });
              if (ctx.canvasRef.current)
                ctx.canvasRef.current.selection = false;
              return;
            }
          }
        }
      }

      // arrows
      if (Array.isArray(ctx.arrows) && ctx.arrows.length) {
        const point = fabricPoint || ctx.getCanvasPoint(event);

        // endpoints first
        for (const a of ctx.arrows) {
          if (isPointNearPoint(point, a.start, 10)) {
            ctx.setSelectedArrowId?.(a.id);
            ctx.setIsDraggingArrow(true);
            ctx.setDraggedArrowId(a.id);
            ctx.setArrowDragMode("start");
            return;
          }
          if (isPointNearPoint(point, a.end, 10)) {
            ctx.setSelectedArrowId?.(a.id);
            ctx.setIsDraggingArrow(true);
            ctx.setDraggedArrowId(a.id);
            ctx.setArrowDragMode("end");
            return;
          }
        }
        // body/shaft
        for (const a of ctx.arrows) {
          if (isPointOnLine(point, a.start, a.end, 8)) {
            ctx.setSelectedArrowId?.(a.id);
            ctx.setIsDraggingArrow(true);
            ctx.setDraggedArrowId(a.id);
            ctx.setArrowDragMode("body");
            // store offset from center for smooth drag
            const cx = (a.start.x + a.end.x) / 2;
            const cy = (a.start.y + a.end.y) / 2;
            ctx.setDragOffset?.({ x: point.x - cx, y: point.y - cy });
            return;
          }
        }
      }
      // Text
      for (const annotation of ctx.textAnnotations) {
        const rectPoly = [
          { x: annotation.position.x, y: annotation.position.y },
          {
            x: annotation.position.x + annotation.width,
            y: annotation.position.y,
          },
          {
            x: annotation.position.x + annotation.width,
            y: annotation.position.y + annotation.height,
          },
          {
            x: annotation.position.x,
            y: annotation.position.y + annotation.height,
          },
        ];

        if (isPointInPolygon(point, rectPoly)) {
          const isDblClick = (event?.detail ?? 1) >= 2;
          const wantsEdit = ctx.activeTool === "text" || isDblClick;

          if (wantsEdit) {
            ctx.setTextAnnotations((prev) =>
              prev.map((t) =>
                t.id === annotation.id ? { ...t, isEditing: true } : t
              )
            );
            ctx.setSelectedText(annotation.id);
            // Make sure Fabric ignores the object while editing (you already skip in render)
            ctx.canvasRef?.current?.upperCanvasEl?.focus?.();
            return;
          }
          // If already selected, check rotate/resize handles first
          if (ctx.selectedText === annotation.id) {
            const bounds = getTextBounds(annotation);
            const rotHandleX = bounds.x + bounds.width / 2;
            const rotHandleY = bounds.y - 25;

            if (isPointNearPoint(point, { x: rotHandleX, y: rotHandleY }, 12)) {
              ctx.setIsRotatingText(true);
              ctx.setSelectedText(annotation.id);
              return;
            }
            const handle = getTextResizeHandle(point, annotation);
            if (handle) {
              ctx.setIsResizingText(true);
              ctx.setTextResizeHandle(handle);
              return;
            }
          }

          // Default: press-anywhere-to-drag (one gesture)
          ctx.setSelectedText(annotation.id);
          ctx.setSelectedMeasurement(null);
          ctx.setSelectedPolygon(null);
          ctx.setSelectedGuideline(null);
          ctx.setSelectedObjectId?.(null);

          // stop Fabric from showing its selection box during drag
          if (ctx.canvasRef.current) ctx.canvasRef.current.selection = false;

          ctx.setIsDraggingText(true);
          ctx.setDraggedTextId(annotation.id);
          ctx.setDragOffset({
            x: point.x - annotation.position.x,
            y: point.y - annotation.position.y,
          });
          return;
        }
      }

      // Guidelines (only if we didn't hit any measurement point/line)
      {
        // if click is on/near any measurement point or line, do NOT allow guideline to grab it
        const hitMeasurement = ctx.measurements.some((m) => {
          if ("start" in m) {
            return (
              isPointNearPoint(point, m.start, 12) ||
              isPointNearPoint(point, m.end, 12) ||
              isPointOnLine(point, m.start, m.end, 8)
            );
          } else {
            // angle: center/arms
            return (
              isPointNearPoint(point, m.center, 12) ||
              isPointNearPoint(point, m.point1, 12) ||
              isPointNearPoint(point, m.point2, 12) ||
              isPointOnLine(point, m.center, m.point1, 8) ||
              isPointOnLine(point, m.center, m.point2, 8)
            );
          }
        });
        // 2) block guideline if click is on/near any editable polygon vertex or edge
        const hitPolygon = (ctx.polygons || []).some((poly) => {
          if (poly.isImport) return false; // imported polys are non-editable anyway
          // vertex hit
          for (let i = 0; i < poly.points.length; i++) {
            if (isPointNearPoint(point, poly.points[i], 10)) return true;
          }
          // edge hit
          if (isPointOnPolygonEdge(point, poly.points, 8)) return true;

          return false;
        });

        if (!hitMeasurement && !hitPolygon) {
          for (const g of ctx.guidelines) {
            if (isPointNearGuideline(point, g, 10)) {
              ctx.setSelectedGuideline(g.id);
              ctx.setSelectedMeasurement(null);
              ctx.setSelectedText(null);
              ctx.setSelectedPolygon(null);
              ctx.setIsDraggingGuideline(true);
              ctx.setDraggedGuidelineId(g.id);
              return;
            }
          }
        }
      }

      // ---------------- Polygon selection (z-index aware) -------------------
      {
        const decorated = (ctx.polygons || []).map((p, idx) => {
          const areaAbs = Math.abs(
            calculatePolygonArea(p.points, 0, 0, ctx.fabricCanvasRef)
          );
          return {
            ref: p,
            idx,
            z: p?.zIndex ?? idx + 1,
            areaAbs,
          };
        });

        const sortTopmost = (a, b) => {
          if (a.z !== b.z) return b.z - a.z;
          if (a.areaAbs !== b.areaAbs) return a.areaAbs - b.areaAbs;
          return b.idx - a.idx;
        };

        const findNearestVertexIndex = (poly, pt, threshold = 10) => {
          let hit = -1;
          for (let i = 0; i < poly.points.length; i++) {
            if (isPointNearPoint(pt, poly.points[i], threshold)) {
              hit = i;
              break;
            }
          }
          return hit;
        };

        const vertexHits = [];
        for (const d of decorated) {
          const poly = d.ref;
          if (poly.isImport) continue;
          const vi = findNearestVertexIndex(poly, point, 10);
          if (vi >= 0) vertexHits.push({ d, vi });
        }
        if (vertexHits.length) {
          vertexHits.sort((A, B) => sortTopmost(A.d, B.d));

          const { d, vi } = vertexHits[0];
          const poly = d.ref;
          ctx.setSelectedPolygon(poly.id);
          ctx.setSelectedObjectId?.(poly.id);
          ctx.setSelectedMeasurement(null);
          ctx.setSelectedText(null);
          ctx.setSelectedGuideline(null);
          ctx.setIsDraggingPolygon(true);
          ctx.setDraggedPolygonId(poly.id);
          ctx._editingIsImport = !!poly.isImport;
          ctx.setDraggedPointIndex(vi);
          return;
        }

        const edgeHits = decorated.filter((d) =>
          isPointOnPolygonEdge(point, d.ref.points, 8)
        );
        if (edgeHits.length) {
          edgeHits.sort(sortTopmost);
          const d = edgeHits[0];
          const poly = d.ref;
          ctx.setSelectedPolygon(poly.id);
          ctx.setSelectedObjectId?.(poly.id);
          ctx.setSelectedMeasurement(null);
          ctx.setSelectedText(null);
          ctx.setSelectedGuideline(null);
          ctx.setIsDraggingPolygon(true);
          ctx.setDraggedPolygonId(poly.id);
          ctx.setDraggedPointIndex(null);
          const cx =
            poly.points.reduce((s, p) => s + p.x, 0) / poly.points.length;
          const cy =
            poly.points.reduce((s, p) => s + p.y, 0) / poly.points.length;
          ctx.setDragOffset({ x: point.x - cx, y: point.y - cy });
          ctx._editingIsImport = !!poly.isImport;
          return;
        }

        const fillHits = decorated.filter(
          (d) => d.ref.isClosed && isPointInPolygon(point, d.ref.points)
        );
        if (fillHits.length) {
          fillHits.sort(sortTopmost);
          const d = fillHits[0];
          const poly = d.ref;
          ctx.setSelectedPolygon(poly.id);
          ctx.setSelectedObjectId?.(poly.id);
          ctx.setSelectedMeasurement(null);
          ctx.setSelectedText(null);
          ctx.setSelectedGuideline(null);
          ctx._editingIsImport = !!poly.isImport;
          return;
        }
      }

      // Lines & angles (select existing)
      for (const m of ctx.measurements) {
        if ("start" in m) {
          if (isPointNearPoint(point, m.start, 10)) {
            ctx.setSelectedMeasurement(m.id);
            ctx.setSelectedObjectId?.(m.id);
            ctx.setSelectedText(null);
            ctx.setSelectedGuideline(null);
            ctx.setSelectedPolygon(null);
            ctx.setIsDragging(true);
            ctx.setDragPoint({ measurementId: m.id, pointType: "start" });
            return;
          } else if (isPointNearPoint(point, m.end, 10)) {
            ctx.setSelectedMeasurement(m.id);
            ctx.setSelectedObjectId?.(m.id);
            ctx.setSelectedText(null);
            ctx.setSelectedGuideline(null);
            ctx.setSelectedPolygon(null);
            ctx.setIsDragging(true);
            ctx.setDragPoint({ measurementId: m.id, pointType: "end" });
            return;
          } else if (isPointOnLine(point, m.start, m.end, 8)) {
            ctx.setSelectedMeasurement(m.id);
            ctx.setSelectedObjectId?.(m.id);
            ctx.setSelectedText(null);
            ctx.setSelectedGuideline(null);
            ctx.setSelectedPolygon(null);
            ctx.setIsDraggingLine(true);
            ctx.setDraggedLineId(m.id);
            ctx.setDragOffset({
              x: point.x - (m.start.x + m.end.x) / 2,
              y: point.y - (m.start.y + m.end.y) / 2,
            });
            return;
          }
        } else {
          const a = m;
          if (isPointNearPoint(point, a.center, 10)) {
            ctx.setSelectedMeasurement(a.id);
            ctx.setSelectedObjectId?.(a.id);
            ctx.setSelectedText(null);
            ctx.setSelectedGuideline(null);
            ctx.setSelectedPolygon(null);
            ctx.setIsDragging(true);
            ctx.setDragPoint({ measurementId: a.id, pointType: "center" });
            return;
          } else if (isPointNearPoint(point, a.point1, 10)) {
            ctx.setSelectedMeasurement(a.id);
            ctx.setSelectedObjectId?.(a.id);
            ctx.setSelectedText(null);
            ctx.setSelectedGuideline(null);
            ctx.setSelectedPolygon(null);
            ctx.setIsDragging(true);
            ctx.setDragPoint({ measurementId: a.id, pointType: "point1" });
            return;
          } else if (isPointNearPoint(point, a.point2, 10)) {
            ctx.setSelectedMeasurement(a.id);
            ctx.setSelectedObjectId?.(a.id);
            ctx.setSelectedText(null);
            ctx.setSelectedGuideline(null);
            ctx.setSelectedPolygon(null);
            ctx.setIsDragging(true);
            ctx.setDragPoint({ measurementId: a.id, pointType: "point2" });
            return;
          } else if (
            isPointOnLine(point, a.center, a.point1, 8) ||
            isPointOnLine(point, a.center, a.point2, 8)
          ) {
            ctx.setSelectedMeasurement(a.id);
            ctx.setSelectedObjectId?.(a.id);
            ctx.setSelectedText(null);
            ctx.setSelectedGuideline(null);
            ctx.setSelectedPolygon(null);
            ctx.setIsDraggingLine(true);
            ctx.setDraggedLineId(a.id);
            ctx.setDragOffset({
              x: point.x - a.center.x,
              y: point.y - a.center.y,
            });
            return;
          }
        }
      }

      // Clear selection
      ctx.setSelectedMeasurement(null);
      ctx.setSelectedText(null);
      ctx.setSelectedGuideline(null);
      ctx.setSelectedPolygon(null);
      ctx.setSelectedObjectId?.(null);
      ctx.setSelectedArrowId?.(null);
    }

    // comments
    for (const c of ctx.comments) {
      if (isPointNearPoint(point, c.position, 15)) {
        // map canvas -> page coords using the canvas rect
        const canvasEl =
          ctx.canvasRef?.current?.lowerCanvasEl ||
          ctx.canvasRef?.current?.upperCanvasEl ||
          ctx.canvasRef?.current;
        const rect = canvasEl?.getBoundingClientRect?.();

        let px = c.position.x;
        let py = c.position.y;
        if (rect && ctx.canvasSize) {
          const sx = rect.width / (Number(ctx.canvasSize.width) || 1);
          const sy = rect.height / (Number(ctx.canvasSize.height) || 1);
          px = rect.left + c.position.x * sx;
          py = rect.top + c.position.y * sy;
        }

        // small UI nudge near the pin
        const pos = { x: px + 12, y: py - 12 };

        // pass page/zoom-root coords upward
        ctx.onShowCommentThread(true, c, pos);
        ctx.onSelectComment(c.id);
        return;
      }
    }

    if (ctx.activeTool === "comment") {
      const canvasEl =
        ctx.canvasRef?.current?.lowerCanvasEl ||
        ctx.canvasRef?.current?.upperCanvasEl ||
        ctx.canvasRef?.current;
      const rect = canvasEl?.getBoundingClientRect?.();

      let px = point.x;
      let py = point.y;
      if (rect && ctx.canvasSize) {
        const sx = rect.width / (Number(ctx.canvasSize.width) || 1);
        const sy = rect.height / (Number(ctx.canvasSize.height) || 1);
        px = rect.left + point.x * sx;
        py = rect.top + point.y * sy;
      }

      ctx.onShowCommentInput(true, { x: px + 12, y: py - 12 });
      return;
    }

    if (ctx.activeTool === "text") {
      const annotation = {
        id: Date.now().toString(),
        position: point,
        text: "",
        width: 150,
        height: 60,
        rotation: 0,
        isEditing: true,

        padding: 2,
        showTextBoxBorder: true,
        textboxBorderColor: "black",
        fill: "yellow",
      };
      ctx.setTextAnnotations((prev) => [...prev, annotation]);
      ctx.setSelectedText(annotation.id);

      ctx.setActiveTool("select");
      ctx.canvasRef?.current?.upperCanvasEl?.focus?.();

      setTimeout(() => ctx.addToHistory(), 0);
      return;
    }

    if (ctx.activeTool === "guideline-vertical") {
      ctx.addGuideline("vertical");
      ctx.setActiveTool("select");
      return;
    }

    if (ctx.activeTool === "guideline-horizontal") {
      ctx.addGuideline("horizontal");
      ctx.setActiveTool("select");
      return;
    }

    // LINEAR
    if (ctx.isLinearTool) {
      if (ctx.currentPoints.length === 0) {
        ctx.setCurrentPoints([point]);
        ctx.setIsDrawing(true);
      } else if (ctx.currentPoints.length === 1) {
        const start = ctx.currentPoints[0];
        const end = point;
        // distance in real units if scale set, else px
        // const distance = distUnits(start, end, {
        //   scale: ctx.scale,
        //   scaleLength: ctx.scaleLength,
        // });
        const distance = computeDistance(start, end, ctx); // px or mm (auto)

        const m = {
          id: Date.now().toString(),
          start,
          end,
          distance,
          unit: isUnitMode(ctx) ? "mm" : "px",
        };
        ctx.setMeasurements((prev) => [...prev, m]);
        ctx.onMeasurementAdd?.(m);
        ctx.setCurrentPoints([]);
        ctx.setIsDrawing(false);
        ctx.addToHistory();
        ctx.setActiveTool("select");
      }
      return;
    }

    // ANGLE
    if (ctx.activeTool === "angle") {
      if (ctx.currentPoints.length < 3) {
        const next = [...ctx.currentPoints, point];

        if (next.length === 3) {
          // next[0] = first endpoint (A)
          // next[1] = vertex/center (B)
          // next[2] = second endpoint (C)
          const center = next[1];
          const p1 = next[0];
          const p2 = next[2];

          const angle = calculateAngle(center, p1, p2);
          const arm1Distance = computeDistance(center, p1, ctx);
          const arm2Distance = computeDistance(center, p2, ctx);

          const m = {
            id: Date.now().toString(),
            center,
            point1: p1,
            point2: p2,
            angle,
            arm1Distance,
            arm2Distance,
            unit: isUnitMode(ctx) ? "mm" : "px",
          };

          ctx.setMeasurements((prev) => [...prev, m]);
          ctx.onMeasurementAdd?.(m);
          ctx.setCurrentPoints([]);

          ctx.addToHistory();
          ctx.setActiveTool("select");
        } else {
          // keep collecting until 3 points (A, B[vertex], C)
          ctx.setCurrentPoints(next);
        }
      }
      return;
    }

    // --- OPEN existing polygon for edit (vertex or edge) ---
    if (
      ctx.activeTool === "open-polygon" ||
      ctx.activeTool === "open-polygons"
    ) {
      const point = fabricPoint || ctx.getCanvasPoint(event);

      // find clicked polygon
      const poly = ctx.polygons.find((p) => isPointInPolygon(point, p.points));
      if (!poly) return;
      if (poly.isImport) {
        return;
      }

      // 🚫 do not allow opening imported polygons for edit
      if (poly.isImport) {
        // (Optional) still mark it selected for UI
        ctx.setSelectedPolygon?.(poly.id);
        ctx.setSelectedObjectId?.(poly.id);
        return;
      }

      // pick split index (prefer vertex, else edge start)
      let idx = -1;
      for (let i = 0; i < poly.points.length; i++) {
        if (isPointNearPoint(point, poly.points[i], 10)) {
          idx = i;
          break;
        }
      }
      if (idx === -1) {
        for (let i = 0; i < poly.points.length; i++) {
          const j = (i + 1) % poly.points.length;
          if (
            isPointOnPolygonEdge(point, [poly.points[i], poly.points[j]], 8)
          ) {
            idx = i;
            break;
          }
        }
      }
      if (idx === -1) return;

      // reorder points so clicked becomes start; drop duplicate closing point
      let reordered = reorderPolygonPoints(poly.points, idx);

      // make it "close with next":
      // i.e., path runs NEXT → ... → CLICKED, so the cut edge is (clicked ↔ next)
      if (reordered.length > 0) {
        reordered = [...reordered.slice(1), reordered[0]];
      }
      if (reordered.length > 1) {
        const a = reordered[0],
          b = reordered[reordered.length - 1];
        if (Math.hypot(a.x - b.x, a.y - b.y) < 1e-3) reordered.pop();
      }

      // remove from CHILD state while editing (so nothing stale renders)
      ctx.setPolygons((prev) => prev.filter((p) => p.id !== poly.id));
      ctx.setMeasurements?.((prev) => prev.filter((m) => m.id !== poly.id));

      // 🔔 tell PARENT to remove the active row now
      ctx.onPolygonEditStart?.(poly.id);

      // keep same id for commit
      ctx.setSelectedObjectId?.(poly.id);

      // enter edit mode
      ctx.setCurrentPoints(reordered);
      ctx.setIsDrawing(true);
      ctx.setActiveTool("polygon");
      return;
    }

    // --- DRAW / COMPLETE polygon (new or editing) ---
    if (ctx.activeTool === "polygon") {
      if (
        ctx.currentPoints.length >= 3 &&
        isPointNearPoint(point, ctx.currentPoints[0], 15)
      ) {
        const area = computeArea(ctx.currentPoints, ctx);
        const perimeter = computePerimeter(ctx.currentPoints, ctx);

        // ✅ reuse id if we were editing; new id only for a brand-new polygon
        const editingId = ctx.selectedObjectId || null;
        const wasImport = editingId ? !!ctx._editingIsImport : false;
        // compute next z-index (topmost)
        const nextZ =
          (Array.isArray(ctx.polygons)
            ? ctx.polygons.reduce((m, p) => Math.max(m, p?.zIndex || 0), 0)
            : 0) + 1;

        const committed = {
          id: editingId || Date.now().toString(),
          points: [...ctx.currentPoints],
          area,
          perimeter,
          areaUnit: isUnitMode(ctx) ? "mm²" : "px²",
          perimeterUnit: isUnitMode(ctx) ? "mm" : "px",
          isClosed: true,
          isImport: false,
          isImport: wasImport, // ← preserve for imported polygons; false for new
          zIndex: wasImport ? -1 : nextZ,
        };

        // add back to CHILD polygons
        ctx.setPolygons((prev) => [...prev, committed]);

        // ensure CHILD measurements has at most one with that id (safety)
        ctx.setMeasurements?.((prev) =>
          prev.filter((m) => m.id !== committed.id)
        );

        // notify PARENT to upsert the row (you already upsert there)
        ctx.onMeasurementAdd?.(committed);

        // cleanup
        ctx.setCurrentPoints([]);
        ctx.setSelectedObjectId?.(null);
        ctx._editingIsImport = undefined; // clear the transient stash

        ctx.setIsDrawing(false);
        ctx.setActiveTool("select");
        ctx.addToHistory?.();
        return;
      } else {
        ctx.setIsDrawing(true);
        ctx.setCurrentPoints((prev) => [...prev, point]);
        return;
      }
    }
  };
}
